import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt } from 'react-icons/fa';
import '../styles/ProjectCard.css';

interface ProjectCardProps {
  id: string;
  title: string;
  cardTitle?: string;  // Optional shorter title for cards
  description: string;
  thumbnail: string;
  tags: string[];
  github?: string;
  featured?: boolean;
  videos?: string[];
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  id,
  title,
  cardTitle,
  description,
  thumbnail,
  tags,
  github,
  featured,
  videos,
}) => {
  // Use cardTitle if provided, otherwise fall back to title
  const displayTitle = cardTitle || title;
  const [isHovered, setIsHovered] = useState(false);

  // Convert video URL to embed URL
  const getEmbedUrl = (videoUrl: string) => {
    if (!videoUrl) return '';
    
    // Handle YouTube URLs
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      let videoId = '';
      let startTime = '';
      
      // Extract timestamp if present (t=180 or t=3m0s format)
      const timeMatch = videoUrl.match(/[?&]t=(\d+)/);
      if (timeMatch) {
        startTime = timeMatch[1];
      }
      // Also handle t=3m30s format
      const timeMatch2 = videoUrl.match(/[?&]t=(\d+)m(\d+)s/);
      if (timeMatch2) {
        startTime = String(parseInt(timeMatch2[1]) * 60 + parseInt(timeMatch2[2]));
      }
      
      if (videoUrl.includes('youtube.com/watch?v=')) {
        videoId = videoUrl.split('v=')[1]?.split('&')[0];
      } else if (videoUrl.includes('youtu.be/')) {
        videoId = videoUrl.split('youtu.be/')[1]?.split('?')[0];
      } else if (videoUrl.includes('youtube.com/embed/')) {
        videoId = videoUrl.split('embed/')[1]?.split('?')[0];
      }
      
      if (videoId) {
        let embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${videoId}&modestbranding=1`;
        if (startTime) {
          embedUrl += `&start=${startTime}`;
        }
        return embedUrl;
      }
    }
    
    // Handle Vimeo URLs
    if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.split('vimeo.com/')[1]?.split('?')[0]?.split('#')[0];
      // Extract timestamp for Vimeo (#t=180s format)
      const vimeoTimeMatch = videoUrl.match(/#t=(\d+)s/);
      let embedUrl = `https://player.vimeo.com/video/${videoId}?autoplay=1&muted=1&loop=1&background=1`;
      if (vimeoTimeMatch) {
        embedUrl += `#t=${vimeoTimeMatch[1]}s`;
      }
      return embedUrl;
    }
    
    // If it's already an embed URL or other direct video URL, return as is
    if (videoUrl.includes('/embed/') || videoUrl.startsWith('https://player.')) {
      return videoUrl;
    }
    
    // For direct video files (mp4, webm, etc.), return the URL
    if (videoUrl.match(/\.(mp4|webm|ogg)$/i)) {
      return videoUrl;
    }
    
    return '';
  };

  // Use the first video in the array if available
  const firstVideoUrl = videos && videos.length > 0 ? videos[0] : '';
  const embedUrl = firstVideoUrl ? getEmbedUrl(firstVideoUrl) : '';
  const isDirectVideo = firstVideoUrl && firstVideoUrl.match(/\.(mp4|webm|ogg)$/i);

  return (
    <motion.div
      className={`project-card ${featured ? 'featured' : ''}`}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Link to={`/projects/${id}`} className="project-card-link">
        <div className="project-thumbnail">
          <img
            src={`${process.env.PUBLIC_URL}${thumbnail}`}
            alt={displayTitle}
            className={`project-thumbnail-img ${isHovered && (embedUrl || isDirectVideo) ? 'hidden' : ''}`}
            onError={(e) => {
              const svg = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250'%3E%3Crect fill='%232a2a30' width='400' height='250'/%3E%3Ctext fill='%232a81b9' font-family='Arial' font-size='24' font-weight='bold' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3E${encodeURIComponent(displayTitle)}%3C/text%3E%3C/svg%3E`;
              e.currentTarget.src = svg;
              e.currentTarget.onerror = null;
            }}
          />
          
          {isHovered && isDirectVideo && (
            <video
              className="project-thumbnail-video"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src={`${process.env.PUBLIC_URL}${firstVideoUrl}`} type={`video/${firstVideoUrl.split('.').pop()}`} />
            </video>
          )}
          
          {isHovered && embedUrl && !isDirectVideo && (
            <iframe
              className="project-thumbnail-video"
              src={embedUrl}
              title={displayTitle}
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen
            />
          )}
          
          {featured && <span className="featured-badge">Featured</span>}
          
          <div className="project-overlay">
            <h3 className="project-title">{displayTitle}</h3>
          </div>
        </div>

        <div className="project-content">
          <div className="project-tags">
            {tags.slice(0, 4).map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </Link>

      <div className="project-footer">
        <Link to={`/projects/${id}`} className="view-details">
          View Details <FaExternalLinkAlt />
        </Link>
        {github && (
          <a
            href={github}
            target="_blank"
            rel="noopener noreferrer"
            className="github-link"
            onClick={(e) => e.stopPropagation()}
          >
            <FaGithub />
          </a>
        )}
      </div>
    </motion.div>
  );
};

export default ProjectCard;

